import { SkFont } from "@shopify/react-native-skia";
import React, { useMemo } from "react";
import { Platform } from "react-native";

import PlatformChartView, {
  createTooltipLabelFormatter,
  filterNativeData,
  makeChartChildrenRenderer,
  PlatformChartData,
  type PaddingConfig,
} from "./PlatformChartView";

/**
 * Props for PlatformChart component
 */
export interface PlatformChartProps {
  /** Array of data objects to be displayed in the chart */
  data: PlatformChartData[];
  /** Key name for x-axis values */
  xKey: string;
  /** Array of keys for y-axis values (multiple lines) */
  yKeys: string[];
  /** Color mapping for each y-axis key */
  colors: Record<string, string>;
  /** Optional human-readable labels for yKeys (for tooltips) */
  yKeyLabels?: Record<string, string>;
  /** Skia font for labels (native only) */
  font?: SkFont | null;
  /** Chart width in pixels (default: 350) */
  width?: number;
  /** Chart height in pixels (default: 250) */
  height?: number;
  /**
   * Padding between the data domain and the chart edges (default: 20).
   *
   * A single number pads all four sides equally. Pass the per-side object form
   * to pad one edge alone — widening all four to buy room on one of them also
   * moves the y domain, and `.nice()` then re-buckets it into a different set
   * of gridlines (SE-7139).
   */
  domainPadding?: number | PaddingConfig;
  /** Padding around the chart (default: 50) */
  padding?: number;
  /** X-axis configuration */
  xAxis?: {
    labelColor?: string;
    fontSize?: number;
    formatXLabel?: (value: string | number) => string;
    tickCount?: number;
    /**
     * Explicit x-axis tick positions (SE-7139).
     *
     * Without these, both chart libraries derive tick positions from
     * `tickCount` alone and let d3-scale pick "nice round numbers", which need
     * not coincide with the values actually plotted. On native that is not
     * merely cosmetic: victory-native draws a label only where it has placed a
     * tick, so a data point whose value d3 skipped renders with nothing beneath
     * it. Supply these whenever the x values are a known, meaningful set (years,
     * for instance) rather than a continuous range.
     *
     * Two contracts the two libraries do NOT share, so mind both:
     *
     * - On native these also DEFINE the x domain (`getDomainFromTicks` wins over
     *   the data in `transformInputData`), so they must span the plotted data or
     *   points fall outside the drawable bounds. On web the tick domain is
     *   unioned with the data domain instead.
     * - Always supply `tickCount` alongside. victory-native defaults it to 5 and
     *   runs `downsampleTicks(tickValues, tickCount)`, so ten-plus ticks without
     *   a count are silently thinned; Victory web short-circuits on a falsy
     *   count and drops nothing.
     *
     * Omit the prop entirely when there is nothing to label. An empty array is
     * not "draw no ticks" — on native it makes the domain `[Infinity, -Infinity]`
     * and every coordinate `NaN` into Skia.
     */
    tickValues?: number[];
    axisValue?: number;
  };
  /** Y-axis configuration */
  yAxis?: {
    labelColor?: string;
    fontSize?: number;
    tickValues?: number[];
    tickFormat?: (value: number) => string;
  }[];
}

/**
 * Container component for platform-aware chart.
 * Handles data filtering, domain calculations, and provides defaults.
 * @param root0 - Component props
 * @param root0.data - Array of data objects containing x values and multiple y values for lines
 * @param root0.xKey - Property name to use for x-axis values from data objects
 * @param root0.yKeys - Array of property names to use for y-axis values (creates multiple lines)
 * @param root0.colors - Object mapping yKey names to hex/named color strings for line colors
 * @param root0.yKeyLabels - Optional object mapping yKey names to human-readable labels for tooltips
 * @param root0.font - Skia font object for rendering labels on native platforms
 * @param root0.width - Chart width in pixels (defaults to 350)
 * @param root0.height - Chart height in pixels (defaults to 250)
 * @param root0.domainPadding - Padding in pixels between data domain and chart edges (defaults to 20)
 * @param root0.padding - Padding in pixels around entire chart area (defaults to 50)
 * @param root0.xAxis - Configuration object for x-axis styling, formatting, and tick settings
 * @param root0.yAxis - Array of configuration objects for y-axis styling and tick settings
 * @remarks
 * This component calculates Y-axis domains and filters data for web,
 * while delegating all rendering logic to the View component.
 */
const PlatformChartContainer: React.FC<PlatformChartProps> = ({
  data,
  xKey,
  yKeys,
  colors,
  yKeyLabels,
  font,
  width = 350,
  height = 250,
  domainPadding = 20,
  padding = 50,
  xAxis,
  yAxis,
}) => {
  // Calculate web-specific data processing
  const webData = useMemo(() => {
    if (Platform.OS !== "web") {
      return { yDomain: undefined, filteredData: new Map() };
    }

    // Calculate domain from all y values to ensure consistent scale
    const allYValues = data.flatMap(d =>
      yKeys
        .map(key => {
          const value = d[key];
          // Only include finite numbers, filter out NaN/Infinity
          return typeof value === "number" && Number.isFinite(value)
            ? value
            : null;
        })
        .filter((val): val is number => val !== null)
    );

    // Guard against empty data
    if (allYValues.length === 0) {
      return { yDomain: undefined, filteredData: new Map() };
    }

    const minY = Math.min(...allYValues);
    const maxY = Math.max(...allYValues);

    // Handle zero-range domains (when all values are identical)
    const range = maxY - minY;
    const yPadding = range > 0 ? range * 0.1 : Math.abs(maxY) * 0.1 || 1;

    // Filter data for each key
    const filteredData = new Map<string, PlatformChartData[]>(
      yKeys.map(key => {
        const validData = data.filter(d => {
          const xValue = d[xKey];
          const yValue = d[key];
          return (
            xValue != null &&
            yValue != null &&
            typeof yValue === "number" &&
            !isNaN(yValue) &&
            isFinite(yValue)
          );
        });
        return [key, validData];
      })
    );

    return {
      yDomain: [minY - yPadding, maxY + yPadding] as [number, number],
      filteredData,
    };
  }, [data, xKey, yKeys]);

  // Calculate mobile-specific padding (converted from number to object format)
  const mobileDomainPadding = useMemo(
    () =>
      typeof domainPadding === "number"
        ? {
            left: domainPadding,
            right: domainPadding,
            top: domainPadding,
            bottom: domainPadding,
          }
        : domainPadding,
    [domainPadding]
  );

  const mobilePadding = useMemo(
    () => ({
      left: padding,
      right: padding,
      top: padding,
      bottom: padding,
    }),
    [padding]
  );

  /**
   * Creates accessor functions for Victory charts
   * Memoized by yKeys to prevent unnecessary recreations
   */
  const accessorsByKey = useMemo(
    () =>
      yKeys.reduce<
        Record<
          string,
          {
            x: (datum: PlatformChartData) => string | number;
            y: (datum: PlatformChartData) => string | number;
          }
        >
      >(
        (acc, key) => ({
          ...acc,
          [key]: {
            x: (datum: PlatformChartData) => datum[xKey],
            y: (datum: PlatformChartData) => datum[key],
          },
        }),
        {}
      ),
    [xKey, yKeys]
  );

  /**
   * Memoized tooltip label formatter for Victory charts
   * Passes full data array so formatter can look up all values at each x position
   */
  const tooltipLabelFormatter = useMemo(
    () => createTooltipLabelFormatter(xKey, yKeys, yKeyLabels, data),
    [xKey, yKeys, yKeyLabels, data]
  );

  /**
   * Render function for CartesianChart children
   * Memoized to prevent unnecessary re-renders
   */
  const renderChartChildren = useMemo(
    () => makeChartChildrenRenderer(yKeys, colors),
    [yKeys, colors]
  );

  // Sparse rows are removed HERE, not in the View: CartesianChart passes every
  // yKey value to Skia as a numeric coordinate, and a row missing a yKey yields
  // `undefined`, which crashes it ("Value is undefined, expected a number").
  const nativeData = filterNativeData(data, yKeys);

  // Handle null case for web when no valid domain
  if (Platform.OS === "web" && !webData.yDomain) {
    return null;
  }

  return (
    <PlatformChartView
      data={data}
      xKey={xKey}
      yKeys={yKeys}
      colors={colors}
      yKeyLabels={yKeyLabels}
      font={font}
      width={width}
      height={height}
      domainPadding={domainPadding}
      padding={padding}
      mobileDomainPadding={mobileDomainPadding}
      mobilePadding={mobilePadding}
      xAxis={xAxis}
      yAxis={yAxis}
      webYDomain={webData.yDomain}
      webFilteredData={webData.filteredData}
      isWeb={Platform.OS === "web"}
      accessorsByKey={accessorsByKey}
      tooltipLabelFormatter={tooltipLabelFormatter}
      renderChartChildren={renderChartChildren}
      nativeData={nativeData}
    />
  );
};

export default PlatformChartContainer;
